import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  VStack,
  HStack,
  Text,
  InputGroup,
  InputRightElement,
  IconButton,
  Alert,
  AlertIcon,
  Link,
  useToast
} from '@chakra-ui/react';
import { FiEye, FiEyeOff, FiExternalLink } from 'react-icons/fi';
import API from '../../helpers/axiosInstance';
import { getApiHeaders } from '../../utils/DefaultFunction';
import { channelIdOf, buildApiConfig } from './providerCatalog';
import { launchWhatsAppEmbeddedSignup } from './whatsappEmbeddedSignup';

/**
 * Renders a connect/configure form for ANY provider manifest and saves a
 * tenant-scoped channel config via /channel_api_config/my-create|my-update.
 * The provider id is stamped into api_config._provider so the catalog page
 * can tell which provider a saved config belongs to.
 */
export default function ProviderConfigForm({ provider, existing, onSaved, onCancel }) {
  const toast = useToast();
  const isCustom = provider.type === 'custom' || provider.fields === 'custom_http';
  const isOauth = provider.type === 'oauth';
  const fields = (isCustom || isOauth) ? [] : (provider.fields || []);

  const initVals = {};
  (fields || []).forEach((f) => { initVals[f.name] = existing?.api_config?.[f.name] ?? ''; });
  const [values, setValues] = useState(initVals);
  const [accountName, setAccountName] = useState(existing?.account_name || provider.name);
  const [show, setShow] = useState({});
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [custom, setCustom] = useState({
    url: existing?.api_config?.url || '',
    method: existing?.api_config?.method || 'Post',
    headers: JSON.stringify(existing?.api_config?.header || existing?.api_config?.headers || {}, null, 2),
    payload: JSON.stringify(existing?.api_config?.payload || {}, null, 2),
  });

  const setVal = (name, v) => setValues((p) => ({ ...p, [name]: v }));
  const onFile = (name, file) => {
    if (!file) return;
    const r = new FileReader();
    r.onload = (e) => setVal(name, e.target.result);
    r.readAsText(file);
  };

  const save = async () => {
    if (!accountName.trim()) { toast({ title: 'Account name is required', status: 'warning', duration: 2500 }); return; }
    let api_config;
    if (isCustom) {
      let header, payload;
      try { header = JSON.parse(custom.headers || '{}'); payload = JSON.parse(custom.payload || '{}'); }
      catch { toast({ title: 'Headers/Payload must be valid JSON', status: 'warning', duration: 3000 }); return; }
      if (!custom.url.trim()) { toast({ title: 'API endpoint URL is required', status: 'warning', duration: 2500 }); return; }
      api_config = { url: custom.url.trim(), method: custom.method, header, payload, _provider: provider.id };
    } else {
      for (const f of fields) {
        if (f.required && !String(values[f.name] ?? '').trim()) {
          toast({ title: `${f.label} is required`, status: 'warning', duration: 2500 }); return;
        }
      }
      api_config = { ...buildApiConfig(provider, values), _provider: provider.id };
    }
    setSaving(true);
    try {
      const payload = {
        channel_id: channelIdOf(provider.channel),
        account_name: accountName.trim(),
        api_config,
        status_id: 1,
        ...(existing?.id ? { id: existing.id } : {}),
      };
      const verb = existing?.id ? 'my-update' : 'my-create';
      await API.post(`${process.env.REACT_APP_API_URL}/channel_api_config/${verb}`, payload, { headers: getApiHeaders() });
      toast({ title: `${provider.name} ${existing?.id ? 'updated' : 'connected'}`, status: 'success', duration: 2500 });
      onSaved && onSaved();
    } catch (e) {
      toast({ title: e?.response?.data?.message || 'Failed to save integration', status: 'error', duration: 3000 });
    } finally { setSaving(false); }
  };

  // WhatsApp Meta Embedded Signup — one-click connect. The App Secret + the
  // code→token exchange happen server-side; the browser only relays the one-time
  // code + the WABA/phone IDs from the FB popup. security: no secret in the frontend.
  const connectWhatsApp = async () => {
    setConnecting(true);
    try {
      const { code, waba_id, phone_number_id } = await launchWhatsAppEmbeddedSignup();
      if (!code) throw new Error('no_code');
      await API.post(
        `${process.env.REACT_APP_API_URL}/channel_api_config/whatsapp/embedded-signup`,
        { code, waba_id, phone_number_id }, { headers: getApiHeaders() });
      toast({ title: 'WhatsApp connected', status: 'success', duration: 2500 });
      onSaved && onSaved();
    } catch (e) {
      const reason = e && e.message;
      const msg = reason === 'not_configured'
        ? 'WhatsApp Embedded Signup isn’t configured yet (missing Meta App ID / config_id).'
        : reason === 'cancelled' ? 'Connection cancelled.'
        : (e && e.response && e.response.data && e.response.data.message) || 'Could not connect WhatsApp. Please try again.';
      toast({ title: msg, status: reason === 'cancelled' ? 'info' : 'error', duration: 3500 });
    } finally { setConnecting(false); }
  };

  const renderField = (f) => {
    const v = values[f.name] ?? '';
    if (f.type === 'select') {
      return (
        <Select size="sm" value={v} placeholder={`Select ${f.label}`} onChange={(e) => setVal(f.name, e.target.value)}>
          {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </Select>
      );
    }
    if (f.type === 'textarea') return <Textarea size="sm" value={v} placeholder={f.placeholder} onChange={(e) => setVal(f.name, e.target.value)} />;
    if (f.type === 'file') return <Input size="sm" type="file" accept={f.accept} pt="3px" onChange={(e) => onFile(f.name, e.target.files?.[0])} />;
    if (f.secret) {
      return (
        <InputGroup size="sm">
          <Input type={show[f.name] ? 'text' : 'password'} value={v} placeholder={f.placeholder} onChange={(e) => setVal(f.name, e.target.value)} />
          <InputRightElement>
            <IconButton size="xs" variant="ghost" aria-label="toggle" icon={show[f.name] ? <FiEyeOff /> : <FiEye />} onClick={() => setShow((p) => ({ ...p, [f.name]: !p[f.name] }))} />
          </InputRightElement>
        </InputGroup>
      );
    }
    const t = f.type === 'number' ? 'number' : f.type === 'email' ? 'email' : f.type === 'tel' ? 'tel' : 'text';
    return <Input size="sm" type={t} value={v} placeholder={f.placeholder} onChange={(e) => setVal(f.name, e.target.value)} />;
  };

  return (
    <VStack align="stretch" spacing={3.5}>
      {provider.note && <Alert status="warning" borderRadius="md" fontSize="xs"><AlertIcon />{provider.note}</Alert>}

      {isOauth && (
        <Box>
          <Alert status="info" borderRadius="md" fontSize="sm" mb={2}><AlertIcon />One-click connect via Meta Embedded Signup — pick your WhatsApp number in the popup; nothing to copy.</Alert>
          <Button w="full" bg="#1877F2" color="white" _hover={{ bg: '#166fe0' }}
            isLoading={connecting} loadingText="Connecting…" onClick={connectWhatsApp}>
            Connect with Facebook
          </Button>
          <Text fontSize="xs" color="gray.500" mt={1.5}>Requires the Meta App ID + Embedded Signup config_id + the backend token-exchange to go live.</Text>
        </Box>
      )}

      {!isOauth && (
        <FormControl isRequired>
          <FormLabel fontSize="sm" mb={1}>Account Name</FormLabel>
          <Input size="sm" value={accountName} placeholder="e.g. Primary account" onChange={(e) => setAccountName(e.target.value)} />
        </FormControl>
      )}

      {isCustom ? (
        <>
          <HStack align="end">
            <FormControl w="30%"><FormLabel fontSize="sm" mb={1}>Method</FormLabel>
              <Select size="sm" value={custom.method} onChange={(e) => setCustom((p) => ({ ...p, method: e.target.value }))}><option>Post</option><option>Get</option></Select>
            </FormControl>
            <FormControl isRequired><FormLabel fontSize="sm" mb={1}>API Endpoint URL</FormLabel>
              <Input size="sm" value={custom.url} placeholder="https://…" onChange={(e) => setCustom((p) => ({ ...p, url: e.target.value }))} />
            </FormControl>
          </HStack>
          <FormControl><FormLabel fontSize="sm" mb={1}>Headers (JSON)</FormLabel>
            <Textarea size="sm" fontFamily="mono" rows={3} value={custom.headers} onChange={(e) => setCustom((p) => ({ ...p, headers: e.target.value }))} />
          </FormControl>
          <FormControl><FormLabel fontSize="sm" mb={1}>Payload (JSON)</FormLabel>
            <Textarea size="sm" fontFamily="mono" rows={4} value={custom.payload} onChange={(e) => setCustom((p) => ({ ...p, payload: e.target.value }))} />
          </FormControl>
        </>
      ) : (
        (fields || []).map((f) => (
          <FormControl key={f.name} isRequired={f.required}>
            <FormLabel fontSize="sm" mb={1}>{f.label}</FormLabel>
            {renderField(f)}
            {f.help && <Text fontSize="xs" color="gray.500" mt={1}>{f.help}</Text>}
          </FormControl>
        ))
      )}

      {provider.docs && (
        <Link href={provider.docs} isExternal fontSize="xs" color="var(--brand-primary)">
          {provider.name} docs <FiExternalLink style={{ display: 'inline', verticalAlign: '-1px' }} />
        </Link>
      )}

      <HStack justify="flex-end" pt={1}>
        <Button size="sm" variant="ghost" onClick={onCancel}>{isOauth ? 'Close' : 'Cancel'}</Button>
        {!isOauth && (
          <Button size="sm" colorScheme="blue" bg="var(--brand-primary)" isLoading={saving} onClick={save}>
            {existing?.id ? 'Save changes' : 'Connect'}
          </Button>
        )}
      </HStack>
    </VStack>
  );
}
